import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { CollectionFilterPreference, CollectionFilterPreferences } from '@kiipu/app-protocol';
import { request } from '../api';
import { isDesktop, platformState, subscribePlatform } from './client-platform';
import { CollectionFilterStore } from './collection-filter-store';

export { CollectionFilterStore, type CollectionFilterSaveStatus } from './collection-filter-store';
export const collectionFilterOwner = (origin: string, userId: string) =>
  JSON.stringify([origin, userId]);
export const collectionFilterPreferencesKey = (owner: string) =>
  ['collection-filter-preferences', owner] as const;
const stores = new Map<string, CollectionFilterStore>();
const stops = new Map<string, () => void>();
let activeOwner = '';
function online() {
  return isDesktop() ? platformState()?.online !== false : navigator.onLine !== false;
}
function storeFor(owner: string, origin: string, userId: string) {
  let value = stores.get(owner);
  if (!value) {
    let storage: Storage | undefined;
    try {
      storage = localStorage;
    } catch {
      /* Storage can be blocked independently of the network. */
    }
    value = new CollectionFilterStore({
      owner,
      storage,
      online: () => {
        if (activeOwner !== owner || !userId) return false;
        const native = platformState();
        if (isDesktop() && (native?.apiOrigin !== origin || native?.user?.id !== userId))
          return false;
        return online();
      },
      load: (signal) =>
        request<CollectionFilterPreferences>('/collection-filter-preferences', { signal }),
      save: (input, signal) =>
        request<CollectionFilterPreference>('/collection-filter-preferences', {
          method: 'PUT',
          body: JSON.stringify(input),
          signal,
          keepalive: true,
        }),
    });
    stores.set(owner, value);
  }
  return value;
}
function start(owner: string, store: CollectionFilterStore) {
  if (stops.has(owner)) return;
  const refresh = () => {
    void store.refresh();
  };
  const resume = () => {
    void store.resume();
  };
  const connection = () => (online() ? resume() : store.synchronize());
  const storage = (event: StorageEvent) => {
    if (event.key?.startsWith(store.storagePrefix)) {
      store.synchronize();
      refresh();
    }
  };
  const pagehide = store.drain;
  window.addEventListener('focus', resume);
  window.addEventListener('online', resume);
  window.addEventListener('offline', connection);
  window.addEventListener('storage', storage);
  window.addEventListener('pagehide', pagehide);
  window.addEventListener('kiipu:collection-filters-changed', refresh);
  const stopPlatform = subscribePlatform(connection);
  stops.set(owner, () => {
    window.removeEventListener('focus', resume);
    window.removeEventListener('online', resume);
    window.removeEventListener('offline', connection);
    window.removeEventListener('storage', storage);
    window.removeEventListener('pagehide', pagehide);
    window.removeEventListener('kiipu:collection-filters-changed', refresh);
    stopPlatform();
  });
  void store.resume();
}
/** Cancels authenticated work without leaking another account's durable pending conditions. */
export function clearCollectionFilterPreferences() {
  activeOwner = '';
  stops.forEach((stop) => stop());
  stops.clear();
  stores.forEach((store) => store.dispose());
  stores.clear();
}
export function useCollectionFilterPreferences(userId: string, origin: string) {
  const owner = collectionFilterOwner(origin, userId);
  // Fail closed before effects run: a timer from the previous identity cannot use the new session.
  activeOwner = owner;
  const store = useMemo(() => storeFor(owner, origin, userId), [owner, origin, userId]);
  const revision = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  useEffect(() => {
    // Keep queues across page mounts, but never across authenticated identity changes.
    for (const [key, previous] of stores) {
      if (key !== owner) {
        stops.get(key)?.();
        stops.delete(key);
        previous.dispose();
        stores.delete(key);
      }
    }
    if (userId) start(owner, store);
  }, [owner, store, userId]);
  return {
    owner,
    revision,
    ready: !userId || store.ready,
    error: store.error,
    current: store.current,
    preference: store.preference,
    status: store.status,
    stage: store.stage,
    retry: store.retry,
    adopt: store.adopt,
    overwrite: store.overwrite,
    refresh: store.refresh,
  };
}
